import {
  animate,
  ImageScaleView,
  ImageScaleViewOpts,
  Transition,
  View,
} from '@play-co/timestep-core/ui';
import { animDuration } from 'src/lib/utils';
import { GenericProgressBar } from './GenericProgressBar';

export class CircularProgressBar extends GenericProgressBar {
  /**
   * Set progress based on the domain.
   */
  public set progress(value: number) {
    this.updateProgress(value);
    this._progress = value;
  }

  private progressFill: [
    ImageScaleView,
    ImageScaleView,
    ImageScaleView,
    ImageScaleView,
  ];

  constructor(
    opts: {
      superview: View;
      frameStyle?: Omit<ImageScaleViewOpts, 'superview'>;
      fillBgStyle?: Omit<ImageScaleViewOpts, 'superview'>;
      fillStyle?: Omit<ImageScaleViewOpts, 'superview'>;
    } & {
      domain?: number[];
      progress?: number;
    },
  ) {
    super({
      superview: opts.superview,
      width: 100,
      height: 100,
      ...(opts.frameStyle || {}),
    });

    const fillBgStyle = {
      superview: this,
      image: 'assets/ui/hud/meters/level_fill_bg.png',
      x: this.style.width / 2,
      y: this.style.height / 2,
      width: 95,
      height: 95,
      centerOnOrigin: true,
      ...(opts.fillBgStyle || {}),
      zIndex: -4,
    };

    const fillStyle = {
      superview: this,
      image: 'assets/ui/hud/meters/level_fill.png',
      x: this.style.width / 2,
      y: this.style.height / 2,
      width: 95,
      height: 95,
      centerOnOrigin: true,
      ...(opts.fillStyle || {}),
      zIndex: -3,
    };

    // Create BG fill.
    new ImageScaleView({
      ...fillBgStyle,
    });
    new ImageScaleView({
      ...fillBgStyle,
      r: Math.PI / 2,
    });
    new ImageScaleView({
      ...fillBgStyle,
      r: Math.PI,
    });
    new ImageScaleView({
      ...fillBgStyle,
      r: Math.PI + Math.PI / 2,
      zIndex: -2,
    });

    // Create progress fill.
    this.progressFill = [
      new ImageScaleView({
        ...fillStyle,
      }),
      new ImageScaleView({
        ...fillStyle,
      }),
      new ImageScaleView({
        ...fillStyle,
      }),
      new ImageScaleView({
        ...fillStyle,
      }),
    ];

    // Create frame.
    new ImageScaleView({
      superview: this,
      image: opts.frameStyle.image ?? 'assets/ui/hud/meters/level_bg.png',
      width: this.style.width,
      height: this.style.height,
      zIndex: 0,
    });

    this._domain = opts.domain ?? [0, 1];
    this.progress = opts.progress ?? 0;
  }

  /**
   * Aniamte current progress to the given value.
   * @param value - Target value.
   * @param duration - Animation duration.
   */
  public async animateProgress(
    value: number,
    duration: number = animDuration,
    easing: Transition = animate.easeInOut,
  ) {
    const initialValue = this._progress;
    this._progress = value;

    await animate(this)
      .then(
        (current) => {
          this.updateProgress(initialValue + current * (value - initialValue));
        },
        duration,
        easing,
      )
      .toPromise();
  }

  public updateFillOpts(opts: Partial<Omit<ImageScaleViewOpts, 'superview'>>) {
    this.progressFill.forEach((x) => x.updateOpts(opts));
  }

  private updateProgress(value: number) {
    const normalizedProgress = this.getNormalizedProgress(value);
    const currentAngle = normalizedProgress * Math.PI * 2;

    for (let i = 0; i < this.progressFill.length; i++) {
      const minAngle = (i * Math.PI) / 2 - Math.PI / 2;
      const maxAngle = ((i + 1) * Math.PI) / 2 - Math.PI / 2;
      const targetRotation = Math.min(
        minAngle + currentAngle - maxAngle,
        maxAngle,
      );

      this.progressFill[i].updateOpts({
        r: targetRotation,
        zIndex:
          i === this.progressFill.length - 1 && targetRotation > 0 ? -1 : -3,
      });
    }
  }
}
